import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { AssessmentControlInput, AssessmentManagerDetail, AssessmentManagerStatus, AssessmentManagerUpsertRequest } from '../../_models/assessmentManager';
import { Project, ProjectControlItem, User } from '../../_models';
import { AlertService, AssessmentManagerService, ProjectService, UserService } from '../../_services';
import { Role } from '../../helpers';

@Component({
  standalone: false,
  selector: 'assessment-manager-edit',
  templateUrl: './assessment-manager-edit.component.html',
  styleUrls: ['./assessment-manager-edit.component.scss']
})
export class AssessmentManagerEditComponent implements OnInit {
  projectId: number;
  assessmentId: number | null;
  project: Project;
  assessment: AssessmentManagerDetail | null = null;

  assessmentName = '';
  leadUserId: number | null = null;
  startDate: Date | null = null;
  endDate: Date | null = null;
  status: AssessmentManagerStatus = AssessmentManagerStatus.Active;

  controlItems: ProjectControlItem[] = [];
  selectedControlItems: ProjectControlItem[] = [];
  controlConfig: { [key: number]: AssessmentControlInput } = {};

  users: User[] = [];
  assessors: User[] = [];

  statusOptions = [
    { label: 'Active', value: AssessmentManagerStatus.Active },
    { label: 'Closed', value: AssessmentManagerStatus.Closed }
  ];

  showValidationErrors = false;
  saving = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private assessmentManagerService: AssessmentManagerService,
    private projectService: ProjectService,
    private userService: UserService,
    public alertService: AlertService
  ) {
    this.projectId = +this.route.snapshot.params['projectId'];
    this.assessmentId = this.route.snapshot.params['assessmentId'] ? +this.route.snapshot.params['assessmentId'] : null;
  }

  ngOnInit() {
    const requests: any[] = [
      this.projectService.getById(this.projectId),
      this.userService.getAllShort()
    ];

    if (this.assessmentId) {
      requests.push(this.assessmentManagerService.getAssessment(this.projectId, this.assessmentId));
    }

    forkJoin(requests).subscribe((data: any[]) => {
      this.project = data[0];
      this.users = data[1] || [];
      this.assessors = this.users.filter(u => (u.role & Role.Assessor) !== 0);
      this.controlItems = (this.project?.projectControlItems || []).slice();

      if (this.assessmentId) {
        this.assessment = data[2];
        this.applyAssessmentDetail(this.assessment);
      }
    });
  }

  applyAssessmentDetail(detail: AssessmentManagerDetail) {
    this.assessmentName = detail.assessmentName;
    this.leadUserId = detail.leadUserId || null;
    this.startDate = detail.startDate ? new Date(detail.startDate) : null;
    this.endDate = detail.endDate ? new Date(detail.endDate) : null;
    this.status = detail.status;

    const selectedIds = new Set<number>();
    detail.controls.forEach(control => {
      selectedIds.add(control.projectControlItemId);
      this.controlConfig[control.projectControlItemId] = {
        assessmentControlId: control.assessmentControlId,
        projectControlItemId: control.projectControlItemId,
        assessorUserId: control.assessorUserId,
        dueDate: control.dueDate ? new Date(control.dueDate) : null,
        status: control.status
      };
    });

    this.selectedControlItems = this.controlItems.filter(c => selectedIds.has(c.projectControlItemId));
  }

  syncSelectedControls() {
    const selectedIds = new Set(this.selectedControlItems.map(c => c.projectControlItemId));
    Object.keys(this.controlConfig).forEach(key => {
      if (!selectedIds.has(+key)) {
        delete this.controlConfig[+key];
      }
    });

    this.selectedControlItems.forEach(control => {
      if (!this.controlConfig[control.projectControlItemId]) {
        this.controlConfig[control.projectControlItemId] = {
          projectControlItemId: control.projectControlItemId,
          assessorUserId: null,
          dueDate: control.dueDate ? new Date(control.dueDate) : null,
          status: 0
        };
      }
    });
  }

  get isAssessmentNameValid(): boolean {
    return (this.assessmentName || '').trim().length > 0;
  }

  get isLeadValid(): boolean {
    return this.leadUserId !== null && this.leadUserId !== undefined;
  }

  get isFormValid(): boolean {
    return this.isAssessmentNameValid && this.isLeadValid;
  }

  saveAssessment() {
    this.showValidationErrors = true;
    this.assessmentName = (this.assessmentName || '').trim();

    if (!this.isFormValid || this.saving) {
      // TODO: figure out where to put constant messages
      this.alertService.error("Please fill in all required fields.");
      return;
    }

    this.saving = true;
    this.syncSelectedControls();

    const controls = this.selectedControlItems.map(control => {
      const config = this.controlConfig[control.projectControlItemId];
      return {
        assessmentControlId: config.assessmentControlId,
        projectControlItemId: control.projectControlItemId,
        assessorUserId: config.assessorUserId,
        dueDate: config.dueDate,
        status: config.status
      } as AssessmentControlInput;
    });

    const payload: AssessmentManagerUpsertRequest = {
      assessmentName: this.assessmentName,
      leadUserId: this.leadUserId || null,
      startDate: this.startDate,
      endDate: this.endDate,
      status: this.status,
      controls: controls
    };

    const request = this.assessmentId
      ? this.assessmentManagerService.updateAssessment(this.projectId, this.assessmentId, payload)
      : this.assessmentManagerService.createAssessment(this.projectId, payload);

    request.subscribe({
      next: (result: AssessmentManagerDetail) => {
        this.saving = false;
        this.router.navigate(['/projects/project', this.projectId, 'assessments']);
      },
      error: (error: Response) => {
        this.saving = false;
        // TODO: revisit exception mechanizm between server and UI
        const errorMessage = (<any>error)?.error?.error || 'An error occurred while getting api keys.';
        this.alertService.error(errorMessage);
      }
    });
  }

  cancel() {
    if (this.assessmentId) {
      this.router.navigate(['/projects/project', this.projectId, 'assessments', this.assessmentId]);
    } else {
      this.router.navigate(['/projects/project', this.projectId, 'assessments']);
    }
  }
}
